import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import { Users, Stethoscope, UtensilsCrossed, Sparkles, CheckCircle, ArrowRight, X } from "lucide-react";
import { Button } from "./ui/button";

const STEPS = [
  { key: "welcome", title: "Welcome to Nutrition Solutions", desc: "Your AI-powered dietitian platform", icon: Sparkles, route: null },
  { key: "add_patient", title: "Add Your First Patient", desc: "Create a patient profile to get started", icon: Users, route: "/patients" },
  { key: "first_session", title: "Record a Session", desc: "Use the AI Scribe to transcribe and organize notes", icon: Stethoscope, route: "/notes" },
  { key: "first_plan", title: "Create a Meal Plan", desc: "Generate an AI-powered nutrition plan", icon: UtensilsCrossed, route: "/create-plan" },
  { key: "explore_features", title: "You're All Set!", desc: "Explore supplements, templates, and more", icon: CheckCircle, route: "/dashboard" },
];

export default function OnboardingWizard() {
  const [status, setStatus] = useState(null);
  const [visible, setVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/onboarding/status").then(r => {
      const data = r.data;
      if (data.completed) { setVisible(false); return; }
      setStatus(data.steps || {});
      // Find the first incomplete step
      const idx = STEPS.findIndex(s => !data.steps?.[s.key]);
      setCurrentStep(Math.max(0, idx));
      setVisible(true);
    }).catch(() => {});
  }, []);

  const dismiss = async () => {
    setVisible(false);
    try { await api.put("/onboarding/status", { completed: true, steps: status }); } catch {}
  };

  const goToStep = (step) => {
    if (step.route) navigate(step.route);
    setVisible(false);
  };

  if (!visible || !status) return null;

  const step = STEPS[currentStep] || STEPS[0];
  const completedCount = STEPS.filter(s => status[s.key]).length;
  const Icon = step.icon;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-300" data-testid="onboarding-wizard">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        {/* Progress bar */}
        <div className="h-1 bg-slate-100 dark:bg-slate-800">
          <div className="h-full bg-forest transition-colors duration-500" style={{ width: `${(completedCount / STEPS.length) * 100}%` }} />
        </div>

        <div className="p-6 text-center">
          <button onClick={dismiss} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 bg-transparent border-0 cursor-pointer">
            <X className="w-5 h-5" />
          </button>

          <div className="w-16 h-16 rounded-2xl bg-forest/10 flex items-center justify-center mx-auto mb-4">
            <Icon className="w-8 h-8 text-forest" />
          </div>

          <h2 className="text-lg font-heading font-bold text-slate-800 dark:text-slate-100 mb-1">{step.title}</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">{step.desc}</p>

          {/* Step indicators */}
          <div className="flex justify-center gap-2 mb-6">
            {STEPS.map((s, i) => (
              <div key={s.key} className={`w-2.5 h-2.5 rounded-full transition-colors ${
                status[s.key] ? "bg-forest" : i === currentStep ? "bg-forest/40 scale-125" : "bg-slate-200 dark:bg-slate-700"
              }`} />
            ))}
          </div>

          <div className="flex gap-3 justify-center">
            {step.route && (
              <Button onClick={() => goToStep(step)} className="bg-forest hover:bg-forest-dark text-white gap-2" data-testid="onboarding-action-btn">
                {step.key === "explore_features" ? "Go to Dashboard" : `Go to ${step.title.split(" ").slice(-1)[0]}`}
                <ArrowRight className="w-4 h-4" />
              </Button>
            )}
            <Button onClick={dismiss} variant="ghost" className="text-slate-500" data-testid="onboarding-skip-btn">
              {step.key === "explore_features" ? "Close" : "Skip for now"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
